import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import { gatewayMgmtMixin } from '@/utils/vuex';
import { themesMixin } from '@/utils/themes';
import map from 'lodash/map';
import get from 'lodash/get';
import moment from 'moment';
export default {
  name: 'NiotaClassicGwMgmtVisualization',
  mixins: [gatewayMgmtMixin, themesMixin],
  props: {
    accountId: {
      type: Number,
      required: true
    },
    filterWarning: {
      type: Boolean,
      required: false,
      default: false
    },
    filterAlert: {
      type: Boolean,
      required: false,
      default: false
    },
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      packetsBar: {},
      rssiGraph: {},
      lsnrGraph: {},
      sfGraph: {},
      loadingMetrics: false,
      graphData: false,
      range: '24h',
      interval: 'hour',
      selectedTimeFrame: {
        value: '24h',
        interval: 'hour',
        name: this.$i18n_t('NiotaClassic.Devices.details.last24')
      },
      timeFrameName: this.$i18n_t('NiotaClassic.Devices.details.last24'),
      intervalId: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions', 'refreshGMMinutes']),
    formCardButtons() {
      return [{
        icon: 'fal fa-times',
        color: 'danger',
        action: 'close',
        tooltip: this.$i18n_t('Common.cancel')
      }];
    },
    timeFrames() {
      return [{
        value: '24h',
        interval: 'hour',
        name: this.$i18n_t('NiotaClassic.Devices.details.last24')
      }, {
        value: '1w',
        interval: 'day',
        name: this.$i18n_t('NiotaClassic.Devices.details.lastWeek')
      }, {
        value: '1M',
        interval: 'day',
        name: this.$i18n_t('NiotaClassic.Devices.details.lastMonth')
      }];
    }
  },
  watch: {
    refreshGMMinutes(newValue) {
      if (this.isFetching) {
        return;
      }
      this.clearGMInterval(this.intervalId);
      if (newValue > 0) {
        let seconds = this.convertMinutesToSeconds(newValue);
        this.intervalId = setInterval(() => {
          this.loadAllMetrics();
        }, seconds * 1000);
      }
    },
    isOpen() {
      if (this.isOpen) {
        this.initData();
      } else {
        this.range = '24h';
        this.interval = 'hour';
      }
    }
  },
  beforeDestroy() {
    this.clearGMInterval(this.intervalId);
  },
  methods: {
    initData() {
      this.loadAllMetrics();
    },
    loadAllMetrics() {
      if (!this.isOpen) {
        return;
      }
      this.loadingMetrics = true;
      const requestData = {
        accountId: this.accountId,
        range: this.range,
        interval: this.interval,
        options: JSON.stringify([this.filterWarning ? 'warning' : null, this.filterAlert ? 'alert' : null].filter(o => o))
      };
      ApiService.gwMgmtLoadAllGatewayMetrics(requestData).then(gateways => {
        if (gateways.length) {
          this.setMetricsData(gateways);
        }
      }).catch(error => {
        this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.loadMetricsFailed'), 'error');
        console.error(error);
      }).finally(() => {
        this.loadingMetrics = false;
      });
    },
    selectTimeFrame(time) {
      this.range = time.value;
      this.interval = time.interval;
      this.timeFrameName = time.name;
      this.loadAllMetrics();
    },
    mapLabelsToLocalTime(labels) {
      return (labels || []).map(date => {
        return moment(new Date(date)).format('LT');
      });
    },
    setMetricsData(gateways) {
      let labels = null;
      // find the first gateway that has correct labels (i.e. has RSSI/LSNR/SF data)
      // all the other gateways have the same time-labels, so only need to find 1 valid set
      let i = 0;
      while (i < gateways.length) {
        labels = get(gateways, `[${i}].rssiLsnrSfMetrics.data.rssi.labels`);
        if (labels && labels.length > 0) {
          break;
        }
        i++;
      }
      let packetMetricsData = {
        labels: [],
        data: []
      };
      let rssiMetricsData = {
        labels: this.mapLabelsToLocalTime(labels),
        metrics: []
      };
      let lsnrMetricsData = {
        labels: this.mapLabelsToLocalTime(labels),
        metrics: []
      };
      let sfMetricsData = {
        labels: this.mapLabelsToLocalTime(labels),
        metrics: []
      };
      let gatewayColours = [];
      map(gateways, gateway => {
        gatewayColours.push(this.rgbToHex(this.randomColor()));
        let countPackets = get(gateway, 'countPackets', 0);
        let rssiMetrics = get(gateway, 'rssiLsnrSfMetrics.data.rssi.data', []);
        let lsnrMetrics = get(gateway, 'rssiLsnrSfMetrics.data.lsnr.data', []);
        let sfMetrics = get(gateway, 'rssiLsnrSfMetrics.data.sf.data', []);
        if (countPackets > 0) {
          packetMetricsData.data.push(countPackets);
          packetMetricsData.labels.push(gateway.title);
        }
        if (rssiMetrics.length) {
          rssiMetricsData.metrics.push({
            data: rssiMetrics,
            label: gateway.title
          });
        }
        if (lsnrMetrics.length) {
          lsnrMetricsData.metrics.push({
            data: lsnrMetrics,
            label: gateway.title
          });
        }
        if (sfMetrics.length) {
          sfMetricsData.metrics.push({
            data: sfMetrics,
            label: gateway.title
          });
        }
      });
      if (packetMetricsData.data.length) {
        this.packetsBar = this.updateBar(packetMetricsData);
        this.rssiGraph = this.updateGraph(rssiMetricsData, gatewayColours);
        this.lsnrGraph = this.updateGraph(lsnrMetricsData, gatewayColours);
        this.sfGraph = this.updateGraph(sfMetricsData, gatewayColours);
        this.graphData = true;
      } else {
        this.graphData = false;
        this.$snack(this.$i18n_t('NiotaClassic.GWMonitoring.snacks.notFound'), 'info');
      }
    },
    updateBar(data) {
      let graphData = {
        common: {
          color: this.vuetifyColorHex(),
          labels: data.labels
        },
        individual: []
      };
      graphData.individual.push({
        data: data.data,
        label: `${this.$i18n_t('NiotaClassic.GWMonitoring.form.sumPackets')} (${this.timeFrameName})`
      });
      return graphData;
    },
    updateGraph(data, colours) {
      let graphData = {
        common: {
          labels: data.labels
        },
        individual: []
      };
      data.metrics.forEach((metric, i) => {
        graphData.individual.push({
          data: metric.data,
          label: metric.label,
          color: colours[i]
        });
      });
      return graphData;
    }
  }
};